/**
 * Shared wiring for the IFC "Differences overview" bulk action (WHA-3695).
 *
 * Owns the selection-derived state both document views need: which selected
 * documents are comparable IFCs, whether the action is available, and the
 * modal's props once it's fired. Extracted from DocumentListTable so the
 * timeline view can offer the same action from the same shared BulkActionBar
 * instead of duplicating the derivation.
 *
 * Candidates are typed structurally so both `DocumentWithTags` (string ids) and
 * the timeline's `DocumentRowMapped` (number ids) satisfy it.
 */

import { useState } from "react";

import { isIfcFile } from "@/domains/documents/constants/fileTypes";

/** The document fields the diff action needs — nothing more. Optional-vs-nullable
 * is deliberately permissive so `DocumentWithTags`, `Document` and the
 * timeline's `DocumentRowMapped` all satisfy it. */
export interface IfcDiffCandidateDoc {
  id: string | number;
  filename: string;
  state?: string | null;
  version?: number | null;
  version_group?: string | null;
  building_id: string | number;
  created_at: string;
}

export interface IfcDiffOverviewState {
  baseDocId: number;
  targetDocId: number;
  baseFilename: string;
  baseVersion: number | null;
  targetFilename: string;
  targetVersion: number | null;
  buildingId: string;
}

interface UseIfcDiffOverviewResult {
  /** True when exactly two processed IFCs are selected. */
  canCompareIfcs: boolean;
  /** Opens the modal for the current selection. No-op when not comparable. */
  handleDiffOverview: () => void;
  /** Modal props while open; null when closed. */
  diffOverview: IfcDiffOverviewState | null;
  closeDiffOverview: () => void;
}

/** An unparsed IFC has no relational data to diff — only IFC_PROCESSED counts. */
function isComparableIfc(d: IfcDiffCandidateDoc): boolean {
  return isIfcFile(d.filename) && d.state === "IFC_PROCESSED";
}

export function useIfcDiffOverview(
  /** Every document the view can resolve a selected id to (duplicates are fine —
   * the same file may appear in both a page slice and a version cache). */
  candidates: IfcDiffCandidateDoc[],
  isSelected: ((docId: string | number) => boolean) | undefined
): UseIfcDiffOverviewResult {
  const [diffOverview, setDiffOverview] = useState<IfcDiffOverviewState | null>(null);

  // Derived every render rather than memoized: `isSelected` reads MST
  // observables, so an `observer` caller already re-renders when the selection
  // changes, and a memo keyed on the selection would need a synthetic dep.
  const selectedIfcs: IfcDiffCandidateDoc[] = [];
  if (isSelected) {
    const seen = new Set<string>();
    for (const doc of candidates) {
      const key = String(doc.id);
      if (seen.has(key)) continue;
      if (isSelected(doc.id) && isComparableIfc(doc)) {
        seen.add(key);
        selectedIfcs.push(doc);
      }
    }
  }

  const canCompareIfcs = selectedIfcs.length === 2;

  const handleDiffOverview = () => {
    if (!canCompareIfcs) return;
    // Order base (older) → target (newer): by version when both carry one, else
    // by created_at.
    const [d1, d2] = selectedIfcs;
    const d1Older =
      d1.version != null && d2.version != null
        ? d1.version <= d2.version
        : new Date(d1.created_at).getTime() <= new Date(d2.created_at).getTime();
    const { base, target } = d1Older ? { base: d1, target: d2 } : { base: d2, target: d1 };

    // Version badges only where they disambiguate: the two docs are versions of
    // the same file (shared version_group), a doc is v2+ (so a v1 necessarily
    // exists), or we can see siblings of its chain among the candidates — the
    // only way a superseded v1 gets selected in the first place. Mirrors the
    // dialog's candidateBadgeVersion so a badge doesn't pop in once the dialog's
    // own candidate list loads.
    const groupCounts = new Map<string, number>();
    for (const d of candidates) {
      if (d.version_group) {
        groupCounts.set(d.version_group, (groupCounts.get(d.version_group) ?? 0) + 1);
      }
    }
    const sameGroup = !!base.version_group && base.version_group === target.version_group;
    const hasKnownSiblings = (d: IfcDiffCandidateDoc) =>
      !!d.version_group && (groupCounts.get(d.version_group) ?? 0) > 1;
    const badgeVersion = (d: IfcDiffCandidateDoc) =>
      d.version != null && (sameGroup || d.version > 1 || hasKnownSiblings(d)) ? d.version : null;

    setDiffOverview({
      baseDocId: Number(base.id),
      targetDocId: Number(target.id),
      baseFilename: base.filename,
      baseVersion: badgeVersion(base),
      targetFilename: target.filename,
      targetVersion: badgeVersion(target),
      buildingId: String(target.building_id),
    });
  };

  return {
    canCompareIfcs,
    handleDiffOverview,
    diffOverview,
    closeDiffOverview: () => setDiffOverview(null),
  };
}
